"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { getVehicleTypeList } from "@/hooks/useCommon";

const cancellationFormScheme = z.object({
  booking_type: z.string().optional(),
  vehicle_type: z.string().optional(),
  type: z.string().optional(),
  cancellations: z
    .array(
      z.object({
        cancellation: z.string().optional(),
        type: z.enum(["value", "%"], {
          errorMap: () => ({ message: "Type must be value or %" }),
        }),
        fees: z.coerce.number().optional(),
      })
    )
    .optional(),
});
const cancellationTypes = [
  "Within 3 Hours",
  "Late cancel",
  "Within 6 Hours",
  "Within 24 Hours",
  "Within 3 Days",
  "Between 3 Days To 7 Days",
  "Between 8 Days To 15 Days",
  "Between 16 Days To 30 Days",
  "Before 30 Days",
  "Before 16 Days",
  "Driver Cancel",
  "User Cancel",
];

const bookingType = [
  "Rental",
  "City Taxi",
  "Airport Transfer",
  "Outstation",
  "One Way",
  "Transport Package",
];

type CancellationFormData = z.infer<typeof cancellationFormScheme>;

export default function CabCancellationFeesForm() {
  const {
    register,
    setValue,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CancellationFormData>({
    resolver: zodResolver(cancellationFormScheme),
  });

  const [cancellationInputCount, setCancellationInputCount] =
    useState<number>(1);

  const { data: GetVehicleList, refetch: vehicleTypeRefetch } =
    getVehicleTypeList();

  const vehicleTypeOptions =
    GetVehicleList?.map((item: any) => ({
      value: item.id,
      label: item.vehicle_type,
    })) || [];

  console.log(vehicleTypeOptions);

  useEffect(() => {
    vehicleTypeRefetch();
  }, []);

  const onSubmit = (data: CancellationFormData) => {
    console.log("Form Submitted:", data);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="p-4 bg-white rounded-sm border border-gray-200 m-6 shadow-md"
    >
      <h3 className="text-sm font-[500] mb-2">Cancellation Fees</h3>

      <div className="grid grid-cols-4 items-center gap-3">
        <div className="flex flex-col space-y-1 mt-2 relative">
          <label className="text-[12px]">Booking Type</label>
          <select
            {...register("booking_type")}
            className={`w-full px-2 py-1 border border-gray-300 rounded-md text-[10px] outline-none bg-white disabled:bg-gray-100 `}
          >
            <option value="">Select Booking Type</option>
            {bookingType.map((item) => (
              <option value={item} key={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col space-y-1 mt-2 relative">
          <label className="text-[12px]">Vehicle Type</label>
          <select
            {...register("type")}
            className={`w-full px-2 py-1 border border-gray-300 rounded-md text-[10px] outline-none bg-white disabled:bg-gray-100 `}
          >
            <option value="">Vehicle Type</option>
            {vehicleTypeOptions.map((item: any) => (
              <option value={item.value} key={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {Array(cancellationInputCount)
        .fill("a")
        .map((_, idx) => (
          <div className="grid grid-cols-4 items-end items-center gap-3">
            <div className="flex flex-col space-y-1 mt-2 relative">
              <label className="text-[12px]">Cancellation</label>
              <select
                {...register(`cancellations.${idx}.cancellation`)}
                className={`w-full px-2 py-1 border border-gray-300 rounded-md text-[10px] outline-none bg-white disabled:bg-gray-100 `}
              >
                <option value="">Select Cancellation</option>
                {cancellationTypes.map((item) => (
                  <option value={item} key={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col space-y-1 mt-2 relative">
              <label className="text-[12px]">Type</label>
              <select
                {...register(`cancellations.${idx}.type`)}
                className={`w-full px-2 py-1 border border-gray-300 rounded-md text-[10px] outline-none bg-white disabled:bg-gray-100 `}
              >
                <option value="">Select</option>
                <option value="value">Value</option>
                <option value="%">%</option>
              </select>
            </div>

            <div className="flex flex-col space-y-1 mt-2 relative">
              <label className="text-[12px]">Fees</label>
              <input
                type="number"
                {...register(`cancellations.${idx}.fees`)}
                placeholder="5"
                className={`w-full px-2 py-1 border border-gray-300 rounded-md text-[10px] outline-none disabled:bg-gray-100 cursor-pointer`}
              />
            </div>

            {idx === 0 && (
              <button
                onClick={() => setCancellationInputCount((prev) => prev + 1)}
                className="cursor-pointer bg-blue-400 hover:bg-blue-500 text-white text-[12px] w-[max-content] px-4 py-1 rounded"
              >
                Add More
              </button>
            )}
            {idx !== 0 && (
              <button
                onClick={() =>
                  setCancellationInputCount((prev) =>
                    prev > 1 ? prev - 1 : prev
                  )
                }
                className="text-red-600 hover:text-red-700 cursor-pointer"
              >
                <Trash2 className="w-6 h-6" />
              </button>
            )}
          </div>
        ))}

      <hr className="my-5 border-t border-gray-200" />

      <div className="flex items-center justify-end w-full mt-3">
        <button
          className={`text-[12px] bg-yellow-500 w-[max-content] px-4 py-1 rounded-sm hover:bg-yellow-600 text-white cursor-pointer`}
        >
          Save & Next
        </button>
      </div>
    </form>
  );
}
